<script setup>
import {useBaseStore} from "~/stores/base";
import Drawer from "~/components/Drawer.vue";
import {productReviewList} from "~/api/api.product";
import ModuleHeader from "~/components/review/ModuleHeader.vue";

const router = useRouter()
const route = useRoute()

/** emits **/
const emits = defineEmits([])

/** props **/
const props = defineProps({})

/** data **/
const open = ref(false)
const pageIndex = ref(1)
const pageSize = ref(20)
const reviews = ref([])
const scrolled = ref(false)
const reviewsRef = ref(null)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const userInfo = computed(() => useBaseStore().getterUserInfo)
const productId = computed(() => route.params.slug[0])
const hash = computed(() => route.hash)

/** watch **/
watch(hash, () => {
  if (hash.value === "#reviews-photos") {
    getData()
    open.value = true
  } else {
    open.value = false
    reset()
  }
})
watch(reviewsRef, () => {
  if (!reviewsRef.value) return
  reviewsRef.value.addEventListener("scroll", (e) => {
    scrolled.value = reviewsRef.value.scrollTop > 0
  })
})

/** methods **/
const handleCloseDrawer = () => {
  // 获取当前路径，但去掉 hash 部分
  router.replace({
    path: router.currentRoute.value.path,
    query: router.currentRoute.value.query, // 保留查询参数
    hash: '' // 移除 hash
  });
}
const getData = async () => {
  const res = await productReviewList({
    id: productId.value,
    pageIndex: pageIndex.value,
    pageSize: pageSize.value,
    onlyImage: true
  })
  reviews.value = reviews.value.concat(res.result.list)
  if (res.result.pageIndex) {
    pageIndex.value++
    await getData()
  }
}
const reset = () => {
  pageIndex.value = 1
  pageSize.value = 20
  reviews.value = []
  scrolled.value = false
}
const handleSelect = id => {
  router.replace({
    path: router.currentRoute.value.path,
    query: router.currentRoute.value.query, // 保留查询参数
    hash: `#reviews-recommend-${id}`
  });
}
const handleViewAll = () => {
  router.replace({
    path: router.currentRoute.value.path,
    query: router.currentRoute.value.query, // 保留查询参数
    hash: '#reviews-all'
  });
}

/** lifecycle **/
onMounted(() => {
  if (hash.value === "#reviews-photos") {
    getData()
    open.value = true
  }
})
</script>

<template>
  <div class="drawer-reviews-mobile" v-if="isMobile">
    <Drawer :open="open" :position="'bottom'" :z-index="4" :global-header-visible="false" @close="handleCloseDrawer">
      <div class="reviews" ref="reviewsRef">
        <div class="sticky" :class="{scrolled}">
          <module-header :title="'customer photos'" @close="handleCloseDrawer"/>
        </div>
        <div class="reviews-content">
          <div class="review" v-for="(review, index) in reviews" :key="index">
            <img class="multi" src="@/assets/img/icon-multi-image.svg" alt="icon" v-if="review.imageSets.length > 1">
            <img class="pic" :src="review.imageSets[0].url" @click="handleSelect(review.id)"/>
          </div>
        </div>
<!--        <div class="btn-secondary" @click="handleViewAll"><span>view more</span></div>-->
      </div>
    </Drawer>
  </div>
  <div class="drawer-reviews-desktop" v-else>
    <Drawer :open="open" :position="'right'" @close="handleCloseDrawer">
      <div class="reviews" ref="reviewsRef">
        <div class="sticky" :class="{scrolled}">
          <module-header :title="'customer photos'" @close="handleCloseDrawer"/>
        </div>
        <div class="reviews-content">
          <div class="review" v-for="(review, index) in reviews">
            <img class="multi" src="@/assets/img/icon-multi-image.svg" alt="icon" v-if="review.imageSets.length > 1">
            <img class="pic" :src="review.imageSets[0].url" @click="handleSelect(review.id)"/>
          </div>
        </div>
<!--        <div class="btn-secondary" @click="handleViewAll"><span>view more</span></div>-->
      </div>
    </Drawer>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.drawer-reviews-mobile {
  .reviews {
    width: 100%;
    height: calc(100vh - $mobile-safari-address-bar-height);
    overflow-y: auto;

    display: flex;
    flex-direction: column;
    gap: 15px;

    position: relative;

    &::-webkit-scrollbar {
      width: 0;
    }

    .sticky {
      position: sticky;
      top: 0;
      z-index: 1;

      background-color: white;
      transition: all .2s ease-in-out;
    }

    .scrolled {
      filter: drop-shadow(0px 1px 20px rgba(0, 0, 0, 0.25));
    }

    .reviews-content {
      padding: 0 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;

      .review {
        width: fit-content;
        height: fit-content;
        cursor: pointer;
        position: relative;
        display: flex;

        .multi {
          position: absolute;
          right: 5px;
          top: 5px;
        }

        .pic {
          width: calc((100vw - 16px * 2 - 4px) / 2);
          height:  calc((100vw - 16px * 2 - 4px) / 2);
          object-position: center center;
          object-fit: cover;
        }
      }
    }

    .btn-secondary {
      width: fit-content;
      padding: 12px 32px;
      margin: 0 auto 150px auto;
    }
  }
}

.drawer-reviews-desktop {
  .reviews {
    width: 375px;
    height: calc(100vh - $desktop-header-height);
    overflow-y: auto;

    display: flex;
    flex-direction: column;
    gap: 15px;

    position: relative;

    &::-webkit-scrollbar {
      width: 0;
    }

    .sticky {
      position: sticky;
      top: 0;
      z-index: 1;

      background-color: white;
      transition: all .2s ease-in-out;
    }

    .scrolled {
      filter: drop-shadow(0px 1px 20px rgba(0, 0, 0, 0.25));
    }

    .reviews-content {
      padding: 0 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;

      .review {
        width: fit-content;
        height: fit-content;
        cursor: pointer;
        position: relative;
        display: flex;

        .multi {
          position: absolute;
          right: 5px;
          top: 5px;
        }

        .pic {
          width: 169px;
          height: 169px;
          object-position: center center;
          object-fit: cover;
        }
      }
    }

    .btn-secondary {
      width: fit-content;
      padding: 12px 32px;
      margin: 0 auto 150px auto;
    }
  }
}
</style>
